import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressHeader from 'components/ui/ProgressHeader';
import FormNavigation from 'components/ui/FormNavigation';
import SessionControls from 'components/ui/SessionControls';
import Icon from 'components/AppIcon';
import DemographicsSection from './components/DemographicsSection';
import HealthStatusSection from './components/HealthStatusSection';
import LifestyleImpactSection from './components/LifestyleImpactSection';
import SymptomsSection from './components/SymptomsSection';
import AnxietyAssessmentSection from './components/AnxietyAssessmentSection';
import CopingMechanismsSection from './components/CopingMechanismsSection';

const MultiStepAssessmentForm = () => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [formData, setFormData] = useState({
    demographics: {},
    healthStatus: {},
    lifestyleImpact: {},
    symptoms: {},
    anxietyAssessment: {},
    copingMechanisms: {}
  });
  const [validationErrors, setValidationErrors] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const sections = [
    { id: 'demographics', title: 'Demographics', component: DemographicsSection },
    { id: 'healthStatus', title: 'Health Status', component: HealthStatusSection },
    { id: 'lifestyleImpact', title: 'Lifestyle Impact', component: LifestyleImpactSection },
    { id: 'symptoms', title: 'Physical Symptoms', component: SymptomsSection },
    { id: 'anxietyAssessment', title: 'Anxiety Assessment', component: AnxietyAssessmentSection },
    { id: 'copingMechanisms', title: 'Coping Mechanisms', component: CopingMechanismsSection }
  ];

  const currentSectionData = sections[currentSection];
  const progressPercentage = Math.round(((currentSection + 1) / sections.length) * 100);

  useEffect(() => {
    // Auto-save functionality
    const autoSaveInterval = setInterval(() => {
      if (hasUnsavedChanges) {
        handleSave();
      }
    }, 30000); // Auto-save every 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [hasUnsavedChanges]);

  const validateCurrentSection = () => {
    const errors = [];
    const sectionId = currentSectionData.id;
    const sectionData = formData[sectionId];

    switch (sectionId) {
      case 'demographics':
        if (!sectionData.age || sectionData.age < 18 || sectionData.age > 120) {
          errors.push('Please enter a valid age between 18 and 120');
        }
        if (!sectionData.gender) {
          errors.push('Please select your gender');
        }
        break;
      case 'healthStatus':
        if (!sectionData.healthcareWorker) {
          errors.push('Please indicate if you are a healthcare worker');
        }
        break;
      case 'lifestyleImpact':
        if (!sectionData.sleepChanges) {
          errors.push('Please indicate changes in your sleep patterns');
        }
        break;
      case 'symptoms':
        if (!sectionData.physicalSymptoms || sectionData.physicalSymptoms.length === 0) {
          errors.push('Please select any physical symptoms you are experiencing');
        }
        break;
      case 'anxietyAssessment':
        if (!sectionData.anxietyLevel) {
          errors.push('Please rate your anxiety level');
        }
        break;
      case 'copingMechanisms':
        if (!sectionData.copingStrategies || sectionData.copingStrategies.length === 0) {
          errors.push('Please select your coping strategies');
        }
        break;
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSectionDataChange = (sectionId, data) => {
    setFormData(prev => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], ...data }
    }));
    setHasUnsavedChanges(true);
    
    // Clear validation errors when user makes changes
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleNext = () => {
    if (validateCurrentSection()) {
      if (currentSection < sections.length - 1) {
        setCurrentSection(prev => prev + 1);
      } else {
        handleComplete();
      }
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async () => {
    setIsLoading(true);
    try {
      // Simulate processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      navigate('/assessment-results-recommendations');
    } catch (error) {
      console.error('Failed to complete assessment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExit = () => {
    navigate('/assessment-welcome-introduction');
  };

  const CurrentSectionComponent = currentSectionData.component;

  return (
    <div className="min-h-screen bg-background">
      {/* Progress Header */}
      <ProgressHeader
        currentStep={2}
        totalSteps={3}
        currentStepTitle="Assessment"
        completionPercentage={66}
      />

      {/* Session Controls */}
      <SessionControls
        onSave={handleSave}
        onExit={handleExit}
        hasUnsavedChanges={hasUnsavedChanges}
        lastSaved={lastSaved}
        isLoading={isLoading}
      />

      {/* Main Content */}
      <div className="pt-32 pb-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Section Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-heading font-semibold text-text-primary">
                Wellness Assessment
              </h1>
              <div className="text-sm text-text-secondary">
                Section {currentSection + 1} of {sections.length}
              </div>
            </div>

            {/* Section Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-text-primary">
                  {currentSectionData.title}
                </span>
                <span className="text-sm text-text-secondary">
                  {progressPercentage}% Complete
                </span>
              </div>
              <div className="progress-indicator">
                <div 
                  className="progress-bar" 
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>

            {/* Section Navigation Dots */}
            <div className="flex items-center justify-center space-x-2 mb-8">
              {sections.map((section, index) => (
                <div
                  key={section.id}
                  className={`
                    w-3 h-3 rounded-full transition-all duration-200
                    ${index < currentSection 
                      ? 'bg-success' 
                      : index === currentSection
                      ? 'bg-primary' :'bg-border'
                    }
                  `}
                />
              ))}
            </div>
          </div>

          {/* Form Section */}
          <div className="bg-surface rounded-xl shadow-therapeutic border border-border">
            <div className="p-8">
              {/* Section Header */}
              <div className="mb-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg">
                    <Icon 
                      name={
                        currentSection === 0 ? "User" :
                        currentSection === 1 ? "Heart" :
                        currentSection === 2 ? "Activity" :
                        currentSection === 3 ? "Thermometer" :
                        currentSection === 4 ? "Brain" : "Shield"
                      } 
                      size={24} 
                      color="var(--color-primary)" 
                    />
                  </div>
                  <div>
                    <h2 className="text-xl font-heading font-semibold text-text-primary">
                      {currentSectionData.title}
                    </h2>
                    <p className="text-text-secondary">
                      {currentSection === 0 && "Tell us about yourself to personalize your assessment"}
                      {currentSection === 1 && "Help us understand your current health status"}
                      {currentSection === 2 && "How has COVID-19 impacted your daily life?"}
                      {currentSection === 3 && "Are you experiencing any physical symptoms?"}
                      {currentSection === 4 && "Let's assess your anxiety and stress levels"}
                      {currentSection === 5 && "How do you currently manage stress and anxiety?"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="mb-6 bg-error-50 border border-error-100 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertCircle" size={20} color="#EF4444" className="mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-error mb-2">
                        Please complete the following:
                      </h3>
                      <ul className="text-sm text-error space-y-1">
                        {validationErrors.map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Current Section Component */}
              <CurrentSectionComponent
                data={formData[currentSectionData.id]}
                onChange={(data) => handleSectionDataChange(currentSectionData.id, data)}
                errors={validationErrors}
              />
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-8 bg-primary-50 border border-primary-100 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} color="#2563EB" className="mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-primary-600 mb-2">
                  Assessment Guidelines
                </h3>
                <ul className="text-sm text-primary-600 space-y-1">
                  <li>• Answer all questions honestly for the most accurate results</li>
                  <li>• Your responses are confidential and securely stored</li>
                  <li>• You can return to previous sections to review your answers</li>
                  <li>• Take breaks as needed - your progress is automatically saved</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Navigation */}
      <FormNavigation
        onPrevious={handlePrevious}
        onNext={handleNext}
        onSave={handleSave}
        canGoBack={currentSection > 0}
        canGoNext={true}
        isLastStep={currentSection === sections.length - 1}
        isLoading={isLoading}
        validationErrors={validationErrors}
        currentSection={currentSectionData.title}
      />
    </div>
  );
};

export default MultiStepAssessmentForm;